import * as THREE from 'three';
import { CELL_SIZE, worldCenterForSubcell } from './mapParkState';
import type { FloorBrushPreview, PlacementPreview } from '../sim/placementPreview';
import type { SubcellAnchor } from '../types';

const PREVIEW_CELL_SIZE = CELL_SIZE / 5;

export const PLACEMENT_PREVIEW_VALID_COLOR = 0x2ec4b6;
export const PLACEMENT_PREVIEW_INVALID_COLOR = 0xe5514f;
export const PLACEMENT_PREVIEW_GHOST_COLOR = 0x9ee8df;

type PreviewMats = {
  valid: THREE.MeshBasicMaterial;
  invalid: THREE.MeshBasicMaterial;
  ghost: THREE.MeshBasicMaterial;
};

export function createInteractionPreviewRoot(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'interactionPreview';
  group.userData.migration = { visual: 'preview' };
  group.userData.interactionPreview = {
    ephemeral: true,
    persistable: false,
    validColor: PLACEMENT_PREVIEW_VALID_COLOR,
    invalidColor: PLACEMENT_PREVIEW_INVALID_COLOR,
  };
  group.userData.previewMats = {
    valid: new THREE.MeshBasicMaterial({
      color: PLACEMENT_PREVIEW_VALID_COLOR,
      transparent: true,
      opacity: 0.46,
      depthWrite: false,
    }),
    invalid: new THREE.MeshBasicMaterial({
      color: PLACEMENT_PREVIEW_INVALID_COLOR,
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
    }),
    ghost: new THREE.MeshBasicMaterial({
      color: PLACEMENT_PREVIEW_GHOST_COLOR,
      transparent: true,
      opacity: 0.28,
      depthWrite: false,
    }),
  } satisfies PreviewMats;
  return group;
}

function clearPreviewMeshes(group: THREE.Group): void {
  for (const child of [...group.children]) {
    group.remove(child);
    if (child instanceof THREE.Mesh) {
      child.geometry.dispose();
    }
  }
}

function addCell(group: THREE.Group, coord: SubcellAnchor, material: THREE.Material, y: number, size: number, height: number): void {
  const world = worldCenterForSubcell(coord);
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(size, height, size), material);
  mesh.position.set(world.x, y, world.z);
  mesh.renderOrder = 6;
  mesh.userData.interactionPreviewCell = true;
  group.add(mesh);
}

function addFootprintGhost(group: THREE.Group, occupied: readonly SubcellAnchor[], material: THREE.Material, y: number): void {
  if (occupied.length === 0) {
    return;
  }
  const points = occupied.map((coord) => worldCenterForSubcell(coord));
  const minX = Math.min(...points.map((point) => point.x));
  const maxX = Math.max(...points.map((point) => point.x));
  const minZ = Math.min(...points.map((point) => point.z));
  const maxZ = Math.max(...points.map((point) => point.z));
  const width = maxX - minX + PREVIEW_CELL_SIZE * 0.86;
  const depth = maxZ - minZ + PREVIEW_CELL_SIZE * 0.86;
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(width, 0.34, depth), material);
  mesh.position.set((minX + maxX) / 2, y, (minZ + maxZ) / 2);
  mesh.renderOrder = 5;
  mesh.userData.interactionPreviewGhost = true;
  group.add(mesh);
}

export function syncInteractionPreview(
  group: THREE.Group,
  preview: PlacementPreview | FloorBrushPreview | null,
  parkTop: number,
): void {
  clearPreviewMeshes(group);
  if (!preview) {
    return;
  }
  const mats = group.userData.previewMats as PreviewMats;
  const tileY = parkTop + 0.2;
  if (preview.mode === 'floor') {
    addCell(
      group,
      preview.coord,
      preview.valid ? mats.valid : mats.invalid,
      tileY,
      PREVIEW_CELL_SIZE * 0.92,
      0.03,
    );
    return;
  }
  const tile = preview.valid ? mats.valid : mats.invalid;
  for (const cell of preview.occupied) {
    addCell(group, cell, tile, tileY, PREVIEW_CELL_SIZE * 0.9, 0.03);
  }
  addFootprintGhost(group, preview.occupied, mats.ghost, parkTop + 0.3);
}
